import { uuidv7 } from "./queue.ts";
import { useEffect, useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api, HttpError } from "./api.ts";
import { Button, ErrorState } from "@ca/ui";
export const mediaUrl = (key: string) =>
  `${(import.meta.env.VITE_MEDIA_ORIGIN as string | undefined) ?? "/media"}/${key}`;
export function useRead<T>(uid: string, path: string, schema: z.ZodType<T>, enabled = true) {
  return useQuery({
    queryKey: ["revision", uid, path],
    queryFn: async () => schema.parse(await api(path)),
    enabled,
  });
}
export function useWrite(uid: string, notify: (text: string) => void) {
  const client = useQueryClient();
  return async (path: string, method: string, body?: unknown) => {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(JSON.stringify([uid, path, method, body ?? null])),
    );
    const storageKey =
      "ca-revision-write:" +
      Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
    const key = sessionStorage.getItem(storageKey) ?? uuidv7();
    sessionStorage.setItem(storageKey, key);
    try {
      const result = await api(path, { method, body, key, accountId: uid });
      sessionStorage.removeItem(storageKey);
      await client.invalidateQueries({ queryKey: ["revision", uid] });
      return result;
    } catch (e) {
      if (e instanceof HttpError && e.status < 500) sessionStorage.removeItem(storageKey);
      notify(e instanceof Error ? e.message : "Chưa lưu được.");
      throw e;
    }
  };
}
export function Html({ value }: { value: string | null | undefined }) {
  return value ? <div dangerouslySetInnerHTML={{ __html: value }} /> : null;
}
export function Failed({ error }: { error: Error | null }) {
  return error ? (
    <ErrorState
      message={error.message}
      onRetry={() => {
        window.location.reload();
      }}
    />
  ) : null;
}
export function Pages({
  offset,
  total,
  limit = 20,
  set,
}: {
  offset: number;
  total: number;
  limit?: number;
  set: (n: number) => void;
}) {
  if (total <= limit && offset === 0) return null;
  return (
    <div className="row" style={{ justifyContent: "center" }}>
      <Button
        variant="soft"
        size="sm"
        disabled={offset === 0}
        onClick={() => {
          set(Math.max(0, offset - limit));
        }}
      >
        ‹ Trước
      </Button>
      <span className="faint num" style={{ fontSize: 12.5, fontWeight: 700 }}>
        {limit === 1
          ? `${offset + 1} / ${total}`
          : total
            ? `${offset + 1}–${Math.min(total, offset + limit)} / ${total}`
            : "0 câu"}
      </span>
      <Button
        variant="soft"
        size="sm"
        disabled={offset + limit >= total}
        onClick={() => {
          set(offset + limit);
        }}
      >
        Sau ›
      </Button>
    </div>
  );
}
export async function speakWord(word: string, accent: "us" | "uk", notify: (text: string) => void) {
  try {
    let data = z
      .object({ key: z.string(), ready: z.boolean() })
      .parse(await api(`/v1/tts/${accent}/${encodeURIComponent(word)}`));
    for (let i = 0; !data.ready && i < 20; i++) {
      if (i === 0) notify("Đang tạo giọng đọc cho từ mới…");
      await new Promise((resolve) => setTimeout(resolve, 1000));
      data = z
        .object({ key: z.string(), ready: z.boolean() })
        .parse(await api(`/v1/tts/${accent}/${encodeURIComponent(word)}`));
    }
    if (!data.ready) {
      notify("Giọng đọc đang được tạo. Hãy thử lại sau.");
      return;
    }
    await new Audio(mediaUrl(data.key)).play();
  } catch (e) {
    notify(e instanceof Error ? e.message : "Chưa phát được giọng đọc.");
  }
}
export function Voices({ word, notify }: { word: string; notify: (text: string) => void }) {
  return (
    <span className="row" style={{ gap: 4, display: "inline-flex" }}>
      <button
        type="button"
        className="chip"
        aria-label={`Phát âm Mỹ: ${word}`}
        onClick={() => void speakWord(word, "us", notify)}
      >
        🔊 US
      </button>
      <button
        type="button"
        className="chip"
        aria-label={`Phát âm Anh: ${word}`}
        onClick={() => void speakWord(word, "uk", notify)}
      >
        🔊 UK
      </button>
    </span>
  );
}
const Definition = z.object({
  word: z.string(),
  ipa: z.string().nullable().optional(),
  meaning: z.string().nullable(),
  example: z.string().nullable().optional(),
  source: z.string(),
  language: z.string(),
});
/** Tra từ khi bấm đúp vào một từ: hộp nhỏ `.vpop` của bản mẫu cạnh chữ vừa chọn. */
export function DictionaryLayer({ uid, notify }: { uid: string; notify: (text: string) => void }) {
  const [word, setWord] = useState("");
  const [meaning, setMeaning] = useState("");
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const write = useWrite(uid, notify);
  const q = useRead(uid, `/v1/dictionary/${encodeURIComponent(word)}`, Definition, !!word);
  useEffect(() => {
    const select = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement) || target.closest("input,textarea,[contenteditable]"))
        return;
      const selection = window.getSelection();
      const text = selection?.toString().trim() ?? "";
      if (/^[a-z][a-z '-]{0,99}$/i.test(text)) {
        const r = selection?.rangeCount ? selection.getRangeAt(0).getBoundingClientRect() : null;
        setPos({
          left: Math.min(innerWidth - 256, Math.max(8, r?.left ?? e.clientX)),
          top: Math.min(innerHeight - 220, (r?.bottom ?? e.clientY) + 8),
        });
        setWord(text);
        setMeaning("");
      }
    };
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") setWord("");
    };
    document.addEventListener("dblclick", select);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("dblclick", select);
      document.removeEventListener("keydown", close);
    };
  }, []);
  if (!word) return null;
  return (
    <div className="vpop" role="dialog" aria-label={word} style={pos}>
      <b>{word}</b>
      <Failed error={q.error} />
      {q.isPending ? (
        <span>Đang tra từ…</span>
      ) : (
        <>
          {q.data?.ipa && <span style={{ opacity: 0.75 }}>{q.data.ipa}</span>}
          {q.data?.meaning ? (
            <span>
              {q.data.language === "en" ? "Nghĩa tiếng Anh: " : ""}
              {q.data.meaning}
            </span>
          ) : (
            <>
              <span>Từ này chưa có trong kho. Nhập nghĩa để lưu.</span>
              <input
                className="input"
                aria-label="Nghĩa của từ"
                style={{ height: 34, color: "var(--ink)" }}
                value={meaning}
                onChange={(e) => {
                  setMeaning(e.target.value);
                }}
              />
            </>
          )}
          {q.data?.example && (
            <span style={{ fontStyle: "italic", opacity: 0.85 }}>{q.data.example}</span>
          )}
          <Voices word={word} notify={notify} />
          <div className="row">
            <Button
              variant="acc"
              size="sm"
              onClick={() =>
                void write(`/v1/me/vocab/${crypto.randomUUID()}`, "PUT", {
                  word,
                  meaning: q.data?.meaning ?? meaning,
                  ipa: q.data?.ipa ?? null,
                  example: q.data?.example ?? null,
                })
                  .then(() => {
                    notify("Đã lưu vào kho và lịch ôn.");
                    setWord("");
                  })
                  .catch(() => {})
              }
            >
              ＋ Lưu vào Kho của tôi
            </Button>
            <button
              className="btn btn-sm"
              style={{ color: "inherit" }}
              onClick={() => {
                setWord("");
              }}
            >
              Đóng
            </button>
          </div>
        </>
      )}
    </div>
  );
}
export function BulkWords({
  words,
  uid,
  notify,
}: {
  words: { term: string; meaningVi: string }[];
  uid: string;
  notify: (text: string) => void;
}) {
  const write = useWrite(uid, notify);
  const [selected, setSelected] = useState(words.map((w) => w.term));
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="eyebrow">📖 Từ vựng nên học</div>
      <div className="row" style={{ gap: 6 }}>
        {words.map((w) => {
          const on = selected.includes(w.term);
          return (
            <button
              key={w.term}
              role="checkbox"
              aria-checked={on}
              className={`chip${on ? " acc" : ""}`}
              style={{ height: "auto", minHeight: 26, padding: "4px 10px", whiteSpace: "normal" }}
              onClick={() => {
                setSelected(on ? selected.filter((x) => x !== w.term) : [...selected, w.term]);
              }}
            >
              {on ? "✓ " : ""}
              <b>{w.term}</b>&nbsp;{w.meaningVi}
            </button>
          );
        })}
      </div>
      <Button
        variant="acc"
        size="sm"
        style={{ alignSelf: "flex-start" }}
        disabled={!selected.length}
        onClick={() =>
          void write("/v1/me/vocab/bulk", "POST", {
            words: words
              .filter((w) => selected.includes(w.term))
              .map((w) => ({ word: w.term, meaning: w.meaningVi })),
          })
            .then(() => {
              notify("Đã lưu các từ đã chọn và đưa vào lịch ôn.");
            })
            .catch(() => {})
        }
      >
        Lưu {selected.length} từ đã chọn
      </Button>
    </div>
  );
}
